import { Gauge } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@mustauto/ui/web';
import { PAINT_THIN_THRESHOLD_UM, getVehiclePanelLabel, type PaintReadings } from '@mustauto/types';
import { describeReadingDates, formatMicrons, pairPanelReadings } from '@mustauto/utils';

/**
 * Laudo de espessura da pintura (F2.3) no detalhe da OS do painel — leitura:
 * quem mede é o mecânico, no aparelho, com o medidor na mão.
 *
 * Uma linha por peça: a menor leitura na entrada e na saída (é a que decide o
 * risco), os pontos medidos e a diferença. Peça abaixo de 90 µm vem marcada.
 */
export function OSPaintReadingsCard({ readings }: { readings: PaintReadings }) {
  const rows = pairPanelReadings(readings);
  if (rows.length === 0) return null;
  const when = describeReadingDates(readings, (d) =>
    d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
  );
  const thinCls = 'font-semibold text-amber-700 dark:text-amber-300';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Gauge size={16} aria-hidden />
          Laudo da pintura
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Espessura em µm, a menor medição de cada peça. Abaixo de {PAINT_THIN_THRESHOLD_UM} µm é pintura fina.
          {when ? ` ${when}.` : ''}
        </p>
      </CardHeader>
      <CardContent>
        <Table className="min-w-[480px]">
          <TableHeader>
            <TableRow className="text-xs uppercase tracking-wide">
              <TableHead className="px-0">Peça</TableHead>
              <TableHead className="px-0 text-right">Entrada</TableHead>
              <TableHead className="px-0 text-right">Saída</TableHead>
              <TableHead className="px-0 text-right">Diferença</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(({ panel, entry: e, exit: x }) => (
              <TableRow key={panel}>
                <TableCell className="px-0 py-2">{getVehiclePanelLabel(panel) ?? panel}</TableCell>
                {[e, x].map((r, i) => (
                  <TableCell key={i} className="px-0 py-2 text-right tabular-nums">
                    {r ? (
                      <>
                        <span className={r.thin ? thinCls : undefined}>
                          {formatMicrons(r.min)}
                          {r.thin ? ' · fina' : ''}
                        </span>
                        <span className="block text-xs text-muted-foreground">{r.values.join(' · ')}</span>
                      </>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                ))}
                <TableCell className="px-0 py-2 text-right tabular-nums text-muted-foreground">
                  {x?.deltaMin != null ? `${x.deltaMin > 0 ? '+' : ''}${x.deltaMin} µm` : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
